import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSkin from "src/hooks/useSkin";
import useSkinPath from "src/hooks/useSkinPath";
import {
  CalendarWrapper,
  CalendarHeader,
  MonthTitle,
  NavButton,
  NavButtons,
  WeekdayHeader,
  WeekdayCell,
  CalendarGrid,
  DayCell,
  DayNumber,
  AssignmentIndicator
} from "./AssignmentCalendar.styles";

interface CalendarAssignment {
  id: number;
  name: string;
  startDate: string;
  dueDate: string;
  state: string;
}

interface AssignmentCalendarProps {
  assignments: CalendarAssignment[];
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();

const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

const isSameDay = (d1: Date, d2: Date) =>
  d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

const ChevronLeft = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const AssignmentCalendar: React.FC<AssignmentCalendarProps> = ({ assignments }) => {
  const [t] = useTranslation();
  const { isGriffle } = useSkin();
  const { path } = useSkinPath();
  const navigate = useNavigate();

  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());

  const handlePrev = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNext = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days: { date: Date; isCurrentMonth: boolean }[] = [];

    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(prevYear, prevMonth, daysInPrevMonth - i),
        isCurrentMonth: false
      });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      days.push({
        date: new Date(currentYear, currentMonth, d),
        isCurrentMonth: true
      });
    }

    const remaining = 42 - days.length;
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    for (let d = 1; d <= remaining; d++) {
      days.push({
        date: new Date(nextYear, nextMonth, d),
        isCurrentMonth: false
      });
    }

    return days;
  }, [currentYear, currentMonth]);

  const assignmentsByDate = useMemo(() => {
    const map = new Map<string, CalendarAssignment[]>();

    assignments.forEach((assignment) => {
      const start = new Date(assignment.startDate);
      const due = new Date(assignment.dueDate);

      const current = new Date(start);
      while (current <= due) {
        const key = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`;
        const list = map.get(key) || [];
        list.push(assignment);
        map.set(key, list);
        current.setDate(current.getDate() + 1);
      }
    });

    return map;
  }, [assignments]);

  const monthName = new Date(currentYear, currentMonth).toLocaleString("default", {
    month: "long",
    year: "numeric"
  });

  return (
    <CalendarWrapper $isGriffle={isGriffle}>
      <CalendarHeader>
        <MonthTitle $isGriffle={isGriffle}>{monthName}</MonthTitle>
        <NavButtons>
          <NavButton
            $isGriffle={isGriffle}
            onClick={handlePrev}
            aria-label={t("previous_month", "Previous month") as string}
          >
            <ChevronLeft />
          </NavButton>
          <NavButton $isGriffle={isGriffle} onClick={handleNext} aria-label={t("next_month", "Next month") as string}>
            <ChevronRight />
          </NavButton>
        </NavButtons>
      </CalendarHeader>

      <WeekdayHeader>
        {WEEKDAYS.map((day) => (
          <WeekdayCell key={day}>{day}</WeekdayCell>
        ))}
      </WeekdayHeader>

      <CalendarGrid>
        {calendarDays.map(({ date, isCurrentMonth }, idx) => {
          const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
          const dayAssignments = assignmentsByDate.get(key) || [];
          const isToday = isSameDay(date, today);

          return (
            <DayCell key={idx} $isGriffle={isGriffle} $isToday={isToday} $isCurrentMonth={isCurrentMonth}>
              <DayNumber $isGriffle={isGriffle} $isToday={isToday}>
                {date.getDate()}
              </DayNumber>
              {dayAssignments.slice(0, 2).map((assignment) => (
                <AssignmentIndicator
                  key={assignment.id}
                  $isGriffle={isGriffle}
                  $state={assignment.state}
                  onClick={() => navigate(path(`/assignments/${assignment.id}`))}
                  title={assignment.name}
                >
                  {assignment.name}
                </AssignmentIndicator>
              ))}
              {dayAssignments.length > 2 && (
                <span style={{ fontSize: 10, color: "var(--color-ink-muted)", paddingLeft: 4 }}>
                  +{dayAssignments.length - 2}
                </span>
              )}
            </DayCell>
          );
        })}
      </CalendarGrid>
    </CalendarWrapper>
  );
};

export default AssignmentCalendar;
